<template>
<div class="payout-tax-list" ref="tax">
    <section class="payout-tax-list__item payout-tax">
      <h2>W-9 Tax Form</h2>
      <validation-observer v-slot="{ handleSubmit, reset }">
        <form @reset.prevent="setSectionNotEditable(reset)"
              @submit.prevent="handleSubmit(submitPayoutMethod)"
        >
          <div class="columns">
            <div class="column is-half">
              <span class="icon-text">
                <div>
                <span :class="iconColor">
                  <i :class="icon"></i>
                </span>
                <span :class="{ 'has-text-success': status === 'Verified' }">{{ status }}</span>
                </div>
                  <p style="margin-top: 0!important;" class="subtitle is-5">{{ description }}</p>
              </span>
            </div>
            <div class="column is-half">
                <div class="buttons is-pulled-right">
                  <b-button class="is-pulled-right" type="is-success"
                            @click="setSectionEditable(true)">Edit</b-button>
                </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </section>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import Vue from 'vue';
import W9TaxForm from '../Modal/W9TaxForm.vue';
import * as CONTRACTOR_ACTIONS from '../../store/modules/contractor/actions';
import * as PARTNER_ACTIONS from '../../store/modules/partner/actions';

const STATUS_NOT_FOUND = 'Not found';

export default {
  name: 'TaxForm',
  data() {
    return {
      statuses: [
        {
          status: STATUS_NOT_FOUND,
          description: 'Your payouts will be held by Enhancify until the required tax form is completed.',
          icon: 'fas fa-exclamation-triangle',
          iconColor: 'icon has-text-danger',
        },
        {
          status: 'Pending',
          description: 'Form W-9 verification can take up to 10 days once your information is submitted. Your payouts will be held by Enhancify until the required tax form is completed.',
          icon: 'fas fa-clock',
          iconColor: 'icon',
        },
        {
          status: 'Verified',
          description: 'Form W-9 submitted on ',
          icon: 'fas fa-check',
          iconColor: 'icon has-text-success',
        },
        {
          status: 'Rejected',
          description: 'You need to get updated W-9. Your payouts will be held by Enhancify until the required tax form is completed.',
          icon: 'fas fa-exclamation-triangle',
          iconColor: 'icon has-text-danger',
        },
      ],
      hasImmediateActionDone: false,
    };
  },
  props: {
    openFormImmediately: {
      type: Boolean,
      default: false,
    },
  },
  activated() {
    this.loadAsyncData();
  },
  deactivated() {
    this.hasImmediateActionDone = false;
  },
  computed: {
    ...mapGetters(['isSwitched', 'userAuthenticator', 'authAuthenticator']),
    /**
     * Automatically determine entity type based on store state
     * @returns {string|null} 'contractor' or 'partner'
     */
    entityType() {
      if (this.$store.state.contractor && this.$store.state.contractor.id) {
        return 'contractor';
      }
      if (this.$store.state.partner && this.$store.state.partner.id) {
        return 'partner';
      }
      return null;
    },
    /**
     * Get entity ID based on entity type
     * @returns {number|null}
     */
    entityId() {
      if (this.entityType === 'contractor') {
        return this.$store.state.contractor.id;
      }
      if (this.entityType === 'partner') {
        return this.$store.state.partner.id;
      }
      return null;
    },
    w9() {
      if (!this.entityType) return { status: null, date: null };
      return this.$store.state[this.entityType].w9;
    },
    status() {
      return this.w9.status || STATUS_NOT_FOUND;
    },
    date() {
      return this.w9.date;
    },
    statusMeta() {
      return this.statuses.find((s) => s.status === this.status) || this.statuses[0];
    },
    description() {
      if (this.statusMeta.status === 'Verified') {
        return this.statusMeta.description + (this.date || '');
      }
      return this.statusMeta.description;
    },
    icon() {
      return this.statusMeta.icon;
    },
    iconColor() {
      return this.statusMeta.iconColor;
    },
  },
  methods: {
    setSectionEditable(modalIsActive) {
      const Component = Vue.extend(W9TaxForm);
      const modal = new Component({
        propsData: {
          entityId: this.entityId,
          entityType: this.entityType,
          active: modalIsActive,
          taxClassificationIsChecked: 0,
        },
      });
      modal.$store = this.$store;
      modal.$mount();
      modal.$on('submit', (response) => {
        if (response) {
          this.$refs.tax.removeChild(modal.$el);
          this.loadAsyncData();
        }
      });
      modal.$on('close', () => {
        this.$refs.tax.removeChild(modal.$el);
      });
      this.$refs.tax.appendChild(modal.$el);
    },
    /**
     * @param reset
     */
    setSectionNotEditable(reset = null) {
      if (typeof reset === 'function') reset();
      this.loadAsyncData();
    },
    async loadAsyncData() {
      if (this.entityId == null || !this.entityType) return;

      const action = this.entityType === 'partner'
        ? `partner/${PARTNER_ACTIONS.LOAD_W9}`
        : `contractor/${CONTRACTOR_ACTIONS.LOAD_W9}`;

      try {
        await this.$store.dispatch(action);
        if (this.openFormImmediately && !this.hasImmediateActionDone) {
          this.setSectionEditable(true);
          this.hasImmediateActionDone = true;
        }
      } catch (e) {
        this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger' });
      }
    },
  },
};
</script>

<style scoped lang="stylus">
.subtitle
  margin-left 25px
  color #293033
  font-size 14px !important
.icon-text
  color #293033
  div
    display flex
    align-items center
  .icon
    width 18px
    height 18px
    font-size 18px
    margin-right 8px
  .has-text-danger
    color #DE4B4B !important
  .has-text-success
    color #68BA62 !important
.payout-tax
  position relative
  .buttons.is-pulled-right
    position absolute
    top 0
    right 0
@media screen and (max-width 480px)
  .payout-tax
    .buttons.is-pulled-right
      position static
      float none !important
      justify-content flex-end

</style>
